// PATH: src/components/questReview/FactChain.tsx
import * as React from "react";
import { Alert, Box, Button, Chip, Link, Skeleton, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useDocumentsApi, useKnowledgeApi, useObjectivesApi } from "../../api";
import type { KnowledgeResponse } from "../../api/knowledge/knowledge";
import type { ObjectiveContextResponse } from "../../api/objectives/objectives";
import type { QueueFacts } from "../../api/questReview/questReview";
import { errorMessage } from "../ui";
import { knowledgeChanged, resolveFacts } from "./factResolution";

/**
 * Le reste de la chaîne : objectif → faits que la génération a reçus →
 * passages du cours d'où ces faits ont été extraits → document. Les faits à
 * gauche, les passages en regard : l'humain voit si l'erreur vient du cours,
 * d'un fait mal extrait, ou du générateur qui s'est écarté des deux.
 *
 * Tout est lu chez tc_embed_be, routes ouvertes au relecteur : l'unité
 * `neighborhood` (elle résout aussi les faits empruntés aux objectifs
 * voisins) et le contexte de l'objectif au voisinage 1 — les passages mêmes
 * que tc_learn donne au générateur.
 */
interface Props {
  objectiveId?: string | null;
  documentId?: string | null;
  facts: QueueFacts;
}

export const FactChain: React.FC<Props> = ({ objectiveId, documentId, facts }) => {
  const { getKnowledge } = useKnowledgeApi();
  const { getObjectiveContext } = useObjectivesApi();
  const { getDocument } = useDocumentsApi();
  const [knowledge, setKnowledge] = React.useState<KnowledgeResponse | null>(null);
  const [context, setContext] = React.useState<ObjectiveContextResponse | null>(null);
  const [docTitle, setDocTitle] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!objectiveId) return;
    setLoading(true);
    setError(null);
    try {
      const [k, c] = await Promise.all([getKnowledge(objectiveId, "neighborhood"), getObjectiveContext(objectiveId, 1)]);
      setKnowledge(k);
      setContext(c.data);
      if (documentId) {
        const d = await getDocument(documentId).catch(() => null);
        setDocTitle(d?.data?.title ?? null);
      }
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger les faits et le cours de l'objectif."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectiveId, documentId]);

  React.useEffect(() => {
    setKnowledge(null);
    setContext(null);
    setDocTitle(null);
    void load();
  }, [load]);

  const chain = React.useMemo(
    () => resolveFacts(knowledge?.unit ?? null, facts.fact_ids, facts.anchor_fact_id),
    [knowledge, facts],
  );
  const passage = (ordinal: number) => context?.chunks.find((c) => c.ordinal === ordinal);

  if (!objectiveId) {
    return <Alert severity="info">Quête sans objectif connu : la chaîne ne peut pas être remontée.</Alert>;
  }
  if (loading) return <Skeleton variant="rounded" height={160} />;
  if (error) {
    return (
      <Alert severity="error" action={<Button onClick={() => void load()}>Réessayer</Button>}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Objectif :
        </Typography>
        <Typography variant="body2" dir="auto">
          {facts.objective_title || context?.objective.text || objectiveId}
        </Typography>
        {documentId && (
          <Link component={RouterLink} to={`/chapters?doc=${documentId}`} variant="body2">
            {docTitle ?? "Ouvrir le chapitre"}
          </Link>
        )}
      </Stack>

      {knowledgeChanged(facts.knowledge_version, knowledge?.source_hash) && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Les faits de cet objectif ont été ré-extraits depuis la génération : les faits montrés ici peuvent ne
          plus être ceux que le générateur a reçus.
        </Alert>
      )}
      {facts.fact_ids.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Aucun fait n'a été journalisé pour cette quête (générée avant le traçage des faits, ou objectif sans
          base de faits) : seuls les passages du cours sont montrés.
        </Alert>
      )}
      {chain.missing.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Faits absents de la base actuelle : <span className="mono">{chain.missing.join(", ")}</span>
        </Typography>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" }, gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            Faits utilisés
          </Typography>
          <Stack spacing={0.75}>
            {chain.facts.map((f) => (
              <Box key={f.id} sx={{ p: 1, borderRadius: 1, border: 1, borderColor: f.anchor ? "primary.main" : "divider" }}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={f.kind} />
                  {f.anchor && <Chip size="small" color="primary" label="interrogé" />}
                  <Typography variant="caption" className="mono" color="text.secondary">
                    {f.id}
                    {f.chunk !== null ? ` · passage ${f.chunk + 1}` : ""}
                  </Typography>
                </Stack>
                <Typography variant="body2" dir="auto" sx={{ mt: 0.5 }}>
                  {f.text}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            Passages du cours
          </Typography>
          <Stack spacing={0.75}>
            {(chain.chunks.length ? chain.chunks : (context?.source_ordinals ?? [])).map((ordinal) => {
              const found = passage(ordinal);
              return (
                <Box key={ordinal} sx={{ p: 1, borderRadius: 1, bgcolor: "background.default" }}>
                  <Typography variant="caption" color="text.secondary">
                    Passage {ordinal + 1}
                  </Typography>
                  {found ? (
                    <Typography variant="body2" dir="auto" sx={{ whiteSpace: "pre-wrap" }}>
                      {found.text}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Hors des passages que le générateur a reçus (voisinage de l'objectif) : ouvrir le chapitre.
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default FactChain;
